'use client';

import { c as _c } from "react/compiler-runtime";
import './article-template.scss';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.ts';
import { programCountryLabels } from '@sola/contentful-model/lib/sac-values.ts';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { ArticleProps } from '@sola/schema/lib/schema.ts';
import { submitSubscription } from '@sola/skippy-sdk/requests.ts';
import { TrackFunnelPage, TrackViewContent } from '@sola/tracking/components/datalayer.tsx';
import { slugify } from '@sola/utils/string.ts';
import { Image } from '../../atoms/image/image.tsx';
import { FSmeta, FSmetaFullBleed, FSmetaFullBleedLight } from '../../mocks/mocks.tsx';
import { ShareButtons } from '../../molecules/share-buttons/share-buttons.tsx';
import { AccordionMain } from '../../organisms-cms/accordion/main/accordion-main.tsx';
import { ArticleListInsert } from '../../organisms-cms/article-list/insert/article-list-insert.tsx';
import { BasicContentMain } from '../../organisms-cms/basic-content/main/basic-content-main.tsx';
import { BasicContentSolo } from '../../organisms-cms/basic-content/solo/basic-content-solo.tsx';
import { DonateShim } from '../../organisms-cms/donate/shim/donate-shim.tsx';
import { GalleryCinema } from '../../organisms-cms/gallery/cinema/gallery-cinema.tsx';
import { JumbotronNotable } from '../../organisms-cms/jumbotron/notable/jumbotron-notable.tsx';
import { JumbotronSidekick } from '../../organisms-cms/jumbotron/sidekick/jumbotron-sidekick.tsx';
import { SacListInsert } from '../../organisms-cms/sac-list/insert/sac-list-insert.tsx';
import { FlexibleSection } from '../../organisms/flexible-section/flexible-section.tsx';
import { FormEmailSubscribe } from '../../organisms/forms/form-email-subscribe/form-email-subscribe.tsx';
interface ArticleTemplateProps {
  article: ArticleProps;
  experiments?: string[];
}
const ArticleSectionsEnum = z.enum(['cta', 'related', 'faq']);
type ArticleSectionsEnum = z.infer<typeof ArticleSectionsEnum>;
const ArticleJumpNav = t0 => {
  const $ = _c(13);
  const {
    sections,
    taxonomyArticleType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["articles", "geo"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t || $[2] !== taxonomyArticleType) {
    t2 = taxonomyArticleType === keys.articleTypes.crisisUpdate ? t("articles:updates") : t("articles:tableOfContents");
    $[1] = t;
    $[2] = taxonomyArticleType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <h2 className="heading heading-xs heading-amplified">{t2}</h2>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== sections) {
    t4 = sections?.map(_temp);
    $[6] = sections;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="Article__sections flex flex-col gap-comfy-xs">{t4}</div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className="Article__sidebar-box pb-comfy-xl grid gap-comfy-sm">{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export const ArticleTemplate: React.FC<ArticleTemplateProps> = t0 => {
  const $ = _c(114);
  const {
    article
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["articles", "geo"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const articleBase = article.taxonomyArticleType ? articlesUrlBase[article.taxonomyArticleType as ArticleTypesEnum] : "#";
  let t2;
  if ($[1] !== article.taxonomyArticleTheme || $[2] !== article.taxonomyArticleType || $[3] !== article.taxonomyProgramCountry || $[4] !== article.taxonomyTopic || $[5] !== articleBase || $[6] !== t) {
    t2 = t3 => {
      const firstOnly = t3 === undefined ? false : t3;
      const tags = [];
      tags.push(<Link key="type" href={`${articleBase}`} title={t(`articles:taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`)}><Badge label={t(`articles:taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`, "articles:taxonomy.articleTypes.type.singularName")} /></Link>);
      const topics = firstOnly ? article.taxonomyTopic?.slice(0, 1) : article.taxonomyTopic;
      topics?.forEach(topic => {
        tags.push(<Link key={`topic-${topic}`} href={`${articleBase}/tag/${slugify(topic)}`} title={t(`articles:taxonomy.articleTopics.${topic}.name`)}><Badge label={t(`articles:taxonomy.articleTopics.${topic}.name`, "articles:taxonomy.articleTopics.topic.name")} /></Link>);
      });
      const countries = firstOnly ? article.taxonomyProgramCountry?.slice(0, 1) : article.taxonomyProgramCountry;
      countries?.forEach(country => {
        const countryLabel = programCountryLabels[country as keyof typeof programCountryLabels];
        tags.push(<Link key={`country-${country}`} href={`${articleBase}/tag/${slugify(countryLabel)}`} title={t(`geo:countries.${country}.nameS`)}><Badge label={t(`geo:countries.${country}.nameS`)} /></Link>);
      });
      const themes = firstOnly ? article.taxonomyArticleTheme?.slice(0, 1) : article.taxonomyArticleTheme;
      themes?.forEach(theme => {
        tags.push(<Link key={`theme-${theme}`} href={`${articleBase}/tag/${slugify(theme)}`} title={t(`articles:taxonomy.articleThemes.${theme}.name`)}><Badge label={t(`articles:taxonomy.articleThemes.${theme}.name`, "articles:taxonomy.articleThemes.theme.name")} /></Link>);
      });
      return tags;
    };
    $[1] = article.taxonomyArticleTheme;
    $[2] = article.taxonomyArticleType;
    $[3] = article.taxonomyProgramCountry;
    $[4] = article.taxonomyTopic;
    $[5] = articleBase;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const renderTaxonomyTags = t2;
  let activeSections;
  let t3;
  if ($[8] !== article.faq || $[9] !== article.secondaryCta) {
    activeSections = [];
    if (article.secondaryCta) {
      activeSections.push(ArticleSectionsEnum.enum.cta);
    }
    activeSections.push(ArticleSectionsEnum.enum.related);
    if (article.faq) {
      activeSections.push(ArticleSectionsEnum.enum.faq);
    }
    t3 = section => {
      const index = activeSections.indexOf(section as ArticleSectionsEnum);
      return {
        ...(index % 2 === 0 ? FSmetaFullBleedLight : FSmetaFullBleed),
        anchor: section
      };
    };
    $[8] = article.faq;
    $[9] = article.secondaryCta;
    $[10] = activeSections;
    $[11] = t3;
  } else {
    activeSections = $[10];
    t3 = $[11];
  }
  const getSectionMeta = t3;
  const hasJumpList = !!article.sections;
  let t4;
  if ($[12] !== article.taxonomyFunnel) {
    t4 = <TrackFunnelPage funnel={article.taxonomyFunnel} />;
    $[12] = article.taxonomyFunnel;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== article.taxonomyArticleTheme) {
    t5 = article.taxonomyArticleTheme ?? [];
    $[14] = article.taxonomyArticleTheme;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== article.taxonomyTopic) {
    t6 = article.taxonomyTopic ?? [];
    $[16] = article.taxonomyTopic;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = [...t5, ...t6];
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== article.contentfulEntryID || $[22] !== article.taxonomyArticleType || $[23] !== article.title || $[24] !== t7) {
    t8 = <TrackViewContent cmsId={article.contentfulEntryID} title={article.title} type={article.taxonomyArticleType} tags={t7} />;
    $[21] = article.contentfulEntryID;
    $[22] = article.taxonomyArticleType;
    $[23] = article.title;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      ...FSmeta,
      anchor: "intro"
    };
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== article.publishDate) {
    t10 = article.publishDate ? new Date(article.publishDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC"
    }) : undefined;
    $[27] = article.publishDate;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = article.title || undefined;
  let t12;
  if ($[29] !== article.summary) {
    t12 = <p>{article.summary}</p>;
    $[29] = article.summary;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12) {
    t13 = <BasicContentSolo seoLevel={1} subtitle={t10} title={t11} body={t12} />;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== renderTaxonomyTags) {
    t14 = <div className="Article__tags flex flex-wrap gap-snug-xl">{renderTaxonomyTags(true)}</div>;
    $[35] = renderTaxonomyTags;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="Article__meta-bar__share" />;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14) {
    t16 = <div className="Article__metabar flex justify-between w-full">{t14}{t15}</div>;
    $[38] = t14;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== article.primaryMedia) {
    t17 = article.primaryMedia?.items && <div className="Article__primary-media"><GalleryCinema {...article.primaryMedia} /></div>;
    $[40] = article.primaryMedia;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t13 || $[43] !== t16 || $[44] !== t17) {
    t18 = <FlexibleSection meta={t9} children1={<div className="Article__intro">{t13}{t16}{t17}</div>} />;
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = article.primaryMedia?.items && article.primaryMedia.items.length > 0;
  let t20;
  if ($[46] !== t19) {
    t20 = {
      ...FSmeta,
      overlap: t19,
      anchor: "article",
      layoutRatio: "2-1"
    };
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== article.intro) {
    t21 = article.intro && <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl"><BasicContentMain seoLevel={2} body={article.intro} inline={true} /></div>;
    $[48] = article.intro;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const t22 = article.body || undefined;
  let t23;
  if ($[50] !== t22) {
    t23 = <BasicContentMain seoLevel={2} body={t22} inline={true} />;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== article.conclusion || $[53] !== article.taxonomyArticleType) {
    t24 = article.conclusion && article.taxonomyArticleType !== keys.articleTypes.crisisUpdate && <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl"><BasicContentMain seoLevel={2} body={article.conclusion} inline={true} /></div>;
    $[52] = article.conclusion;
    $[53] = article.taxonomyArticleType;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== article.conclusion || $[56] !== article.taxonomyArticleType) {
    t25 = article.conclusion && article.taxonomyArticleType === keys.articleTypes.crisisUpdate && <BasicContentMain seoLevel={2} body={article.conclusion} inline={true} />;
    $[55] = article.conclusion;
    $[56] = article.taxonomyArticleType;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== renderTaxonomyTags) {
    t26 = <div className="Article__tags mt-comfy-xs flex flex-wrap gap-snug-xl">{renderTaxonomyTags(false)}</div>;
    $[58] = renderTaxonomyTags;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== article.summary || $[61] !== article.title) {
    t27 = <div className="Article__share"><ShareButtons shareTitle={article.title} shareSummary={article.summary} /></div>;
    $[60] = article.summary;
    $[61] = article.title;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27) {
    t28 = <div className="Article__footer flex flex-row justify-between">{t26}{t27}</div>;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t21 || $[67] !== t23 || $[68] !== t24 || $[69] !== t25 || $[70] !== t28) {
    t29 = <article className="Article__article body-md flex flex-col gap-comfy-xl">{t21}{t23}{t24}{t25}{t28}</article>;
    $[66] = t21;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== article || $[73] !== hasJumpList) {
    t30 = hasJumpList && <div className="Article__sidebar-sections Article__sidebar-box"><ArticleJumpNav {...article} /></div>;
    $[72] = article;
    $[73] = hasJumpList;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== article.contributors || $[76] !== article.taxonomyArticleType || $[77] !== t) {
    t31 = article.taxonomyArticleType !== keys.articleTypes.pressRelease && <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start">{article.contributors && article.contributors.length > 0 && <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm"><h2 className="heading heading-xs">{t("articles:contributors")}</h2><div className="Article__contributors flex flex-col gap-comfy-sm">{article.contributors.map(_temp2)}</div></div>}<div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm"><FormEmailSubscribe className="max-w-md" title={t("articles:subscribeTitle")} subscriptionCode="Leads_General_Sign_Up" onSubmit={submitSubscription} subscribeDirectionForm="column" showInputsPlaceholder={true} titleSize="xs" /></div></div>;
    $[75] = article.contributors;
    $[76] = article.taxonomyArticleType;
    $[77] = t;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== article.taxonomyArticleType || $[80] !== t) {
    t32 = article.taxonomyArticleType === keys.articleTypes.pressRelease && <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start"><div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm border-b-1"><h2 className="heading heading-xs pb-snug-lg">{t("articles:aboutCompassionTitle")}</h2><div className="flex flex-col gap-comfy-xs body body-sm">{t("articles:aboutCompassionBody")}</div></div><div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm"><h2 className="heading heading-xs">{t("articles:socialMedia.follow")}</h2><div className="flex flex-col gap-comfy-lg"><Link href="https://www.facebook.com/compassionintl" target="blank" title="Compassion International - Facebook"><ArkIcon iconName="facebook" size="24" />{t("articles:socialMedia.facebook")}</Link><Link href="https://twitter.com/compassion" target="blank" title="Compassion International - X (Twitter)"><ArkIcon iconName="twitter" size="24" />{t("articles:socialMedia.twitter")}</Link><Link href="https://instagram.com/compassion" target="blank" title="Compassion International - Instagram"><ArkIcon iconName="instagram" size="24" />{t("articles:socialMedia.instagram")}</Link><Link href="https://www.tiktok.com/@compassioninternational" target="blank" title="Compassion International - TikTok"><ArkIcon iconName="tiktok" size="24" />{t("articles:socialMedia.tiktok")}</Link></div></div></div>;
    $[79] = article.taxonomyArticleType;
    $[80] = t;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== article.primaryCta) {
    t33 = article.primaryCta && ("fund" in article.primaryCta ? <DonateShim {...{
      ...article.primaryCta,
      body: undefined
    }} /> : <JumbotronNotable {...article.primaryCta} />);
    $[82] = article.primaryCta;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t30 || $[85] !== t31 || $[86] !== t32 || $[87] !== t33) {
    t34 = <aside className="Article__sidebar flex flex-col gap-comfy-xl">{t30}{t31}{t32}{t33}</aside>;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== t20 || $[90] !== t29 || $[91] !== t34) {
    t35 = <FlexibleSection meta={t20} children1={t29} children2={t34} />;
    $[89] = t20;
    $[90] = t29;
    $[91] = t34;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== activeSections || $[94] !== article.secondaryCta || $[95] !== getSectionMeta) {
    t36 = activeSections.includes(ArticleSectionsEnum.enum.cta) && article?.secondaryCta && <FlexibleSection meta={getSectionMeta(ArticleSectionsEnum.enum.cta)} children1={"limit" in article.secondaryCta ? <SacListInsert {...article.secondaryCta} /> : <JumbotronSidekick seoLevel={2} {...article.secondaryCta} />} />;
    $[93] = activeSections;
    $[94] = article.secondaryCta;
    $[95] = getSectionMeta;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== activeSections || $[98] !== article || $[99] !== getSectionMeta || $[100] !== t) {
    t37 = activeSections.includes(ArticleSectionsEnum.enum.related) && <FlexibleSection meta={getSectionMeta(ArticleSectionsEnum.enum.related)} children1={<><BasicContentSolo seoLevel={2} title={t("articles:relatedContent")} /><ArticleListInsert seoLevel={3} relatedArticle={article} /></>} />;
    $[97] = activeSections;
    $[98] = article;
    $[99] = getSectionMeta;
    $[100] = t;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  let t38;
  if ($[102] !== activeSections || $[103] !== article.faq || $[104] !== getSectionMeta) {
    t38 = activeSections.includes(ArticleSectionsEnum.enum.faq) && <FlexibleSection meta={getSectionMeta(ArticleSectionsEnum.enum.faq)} children1={<AccordionMain seoLevel={2} {...article.faq} />} />;
    $[102] = activeSections;
    $[103] = article.faq;
    $[104] = getSectionMeta;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  let t39;
  if ($[106] !== t18 || $[107] !== t35 || $[108] !== t36 || $[109] !== t37 || $[110] !== t38 || $[111] !== t4 || $[112] !== t8) {
    t39 = <div className="Article Article--blog ">{t4}{t8}{t18}{t35}{t36}{t37}{t38}</div>;
    $[106] = t18;
    $[107] = t35;
    $[108] = t36;
    $[109] = t37;
    $[110] = t38;
    $[111] = t4;
    $[112] = t8;
    $[113] = t39;
  } else {
    t39 = $[113];
  }
  return t39;
};
function _temp(section) {
  return <Link key={section.id} href={`#${section.id}`}><h3 className="label-lg label-potent">{section.title}</h3></Link>;
}
function _temp2(contributor) {
  return <div key={contributor.name} className="label-lg label-moderate flex gap-comfy-xs">{contributor.image?.url && <Image image={{
      url: contributor.image.url,
      alt: contributor.name
    }} aspectRatio="1" rounded="full" className="Contributor__image" />}<div className="Contributor__info flex flex-col"><h3 className="Contributor__heading label-md label-potent">{contributor.name}</h3><p>{contributor.position}</p></div></div>;
}